<template>
  <div class="order-detail align-left">
    <div>
      <span v-if="!hasItemsSelectedForCheckout" class="mr-2">
        {{ $t('checkout.ordDet.noItemsSelected') }}
      </span>
      <span class="primary-link" @click="selectAllItemsForCheckout()" >
        {{ $t('checkout.ordDet.selectAll') }}
      </span>
    </div>
    <ul class="orders">
      <li v-for="(item, itemIndex) in orders" v-bind:key="itemIndex">
        <b-row>
          <!-- Checkbox, product image and optional span -->
          <b-col cols="4" lg="4" xl="2">
            <div class="position-relative">
              <!-- Not available span -->
              <span v-if="!isItemAvailableForPurchase(item)" class="not-available">
                {{ $t('notAvailable') }}
              </span>

              <!-- Checkbox and img -->
              <div :class="{'mask': !isItemAvailableForPurchase(item)}">
                <b-form-checkbox
                  :id="`checkbox-${itemIndex}`"
                  v-model="item.selectedForCheckout"
                  :name="`checkbox-${itemIndex}`"
                  :value="true"
                  :unchecked-value="false"
                  :disabled="!isItemAvailableForPurchase(item)"
                  class="checkbox-item"
                  @change="changeItemSelectedStatus(item, itemIndex)"
                />
                <img
                  :src="item.product.thumbnailUrls[0]"
                  alt="broken"
                  class="order-img order-desc"
                  @click="gotoProduct(item.product)"
                >
              </div>
            </div>
          </b-col>

          <!-- Item details -->
          <b-col cols="8" lg="8" xl="10">
            <div class="order-desc mb-3" :class="{'mask': !isItemAvailableForPurchase(item)}">
              <!-- Name & Price -->
              <b-row>
                <b-col cols="12" lg="8" xl="9">
                  <span>{{item.product.name}}</span>
                </b-col>
                <b-col cols="12" lg="4" xl="3" class="item-price" v-if="isItemAvailableForPurchase(item)">
                  <span class="font-weight-bold"> {{ $n(item.aggregatedPrice.amount, 'currency') }}</span>
                  <span class="d-block unit-price">
                    {{ $n(item.product.marked_price.amount, 'currency') }}/{{ $t('checkout.ordDet.unit') }}
                  </span>
                </b-col>
              </b-row>
              
              <!-- Customizations -->
              <div style="font-size: 12px; margin-bottom: 0.5rem;">
                <p
                  v-for="([key, val], cid) of Object.entries(item.customizations)"
                  v-bind:key="cid"
                  class="mb-1"
                >
                  <span style="font-size: 0.85rem; font-weight: 800;">{{key}}</span>: <span>{{ val }}</span>
                </p>
              </div>

              <!-- Counts selection -->
              <b-form-select
                size="sm"
                style="width: 100px;"
                v-if="isItemAvailableForPurchase(item)"
                v-model="item.counts"
                :options="countOptions"
                @change.native="updateCartItem(item)"
              />
            </div>

            <!-- Delete button -->
            <span class="delete" @click="deleteSelected(item)">{{ $t('delete') }}</span>
          </b-col>
        </b-row>
      </li>
    </ul>
    <hr>

    <div class="total-line" v-if="orders && orders.length > 0">
      <b-row>
        <!-- <hr> -->
        <b-col cols="6" lg="8" class="align-right">
          <strong>{{ $t('checkout.ordDet.totalWeight') }}</strong>
        </b-col>
        <b-col
          class="align-right"
        >{{parseFloat(totalWeight.quantity).toFixed(2)}} {{totalWeight.unit}}</b-col>
      </b-row>

      <b-row>
        <!-- <hr> -->
        <b-col cols="6" lg="8" class="align-right">
          <strong>{{ $t('subtotal') }}</strong>
        </b-col>
        <b-col class="align-right">{{ $n(subtotal.amount, 'currency') }}</b-col>
      </b-row>

      <b-row v-if="serviceCharge">
        <!-- <hr> -->
        <b-col cols="8" class="align-right">
          <strong>{{ $t('checkout.ordDet.servCharge') }}</strong>
        </b-col>
        <b-col
          class="align-right"
        >{{ $n(serviceCharge.amount, 'currency') }}</b-col>
      </b-row>

      <b-row v-if="shippingPrice">
        <!-- <hr> -->
        <b-col cols="8" class="align-right">
          <strong>{{ $t('checkout.ordDet.shippCharge') }}</strong>
        </b-col>
        <b-col
          class="align-right"
        >{{ $n(shippingPrice.amount, 'currency') }}</b-col>
      </b-row>

      <b-row v-if="tariffPrice">
        <!-- <hr> -->
        <b-col cols="8" class="align-right">
          <strong>{{ $t('checkout.ordDet.tariffCharge') }}</strong>
        </b-col>
        <b-col
          class="align-right"
        >{{ $n(tariffPrice.amount, 'currency') }}</b-col>
      </b-row>

      <br>
      <b-row v-if="cartTotal">
        <!-- <hr> -->
        <b-col cols="8" class="align-right">
          <strong>{{ $t('total') }}</strong>
        </b-col>
        <b-col class="align-right">
          <strong> {{ $n(cartTotal.amount, 'currency') }}</strong>
        </b-col>
      </b-row>
    </div>

    <div v-if="orders && orders.length <= 0" class="order-empty">
      <div class="content">
        <div>
          {{ $t('checkout.ordDet.emptyOrd') }}
          <br>
          <br>
          <b-button @click="gotoDealPage()" class="primary-button">
            {{ $t('checkout.ordDet.goToHomePg') }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import notification from '@/services/notificationService';

export default {
  name: 'OrderDetail',
  data() {
    return {
      countOptions: [],
      items_backup: []
    };
  },

  created() {
    this.countOptions = Array.from(Array(200).keys(), val => val + 1);
  },

  computed: {
    ...mapGetters({
      orders: 'cartStore/getCart',
      cartTotal: 'cartStore/getTotal',
      subtotal: 'cartStore/getSubTotal',
      serviceCharge: 'cartStore/getServiceCharge',
      shippingPrice: 'cartStore/getShippingPrice',
      tariffPrice: 'cartStore/getTariffPrice',
      totalWeight: 'cartStore/getTotalWeight',
    }),

    // Verify if item's product is available for purchase
    isItemAvailableForPurchase() {
      return item => {
        return (item.product.active && item.product.published && !item.product.fixesRequired);
      }
    },

    // Verify if there's at least one item selected for checkout
    hasItemsSelectedForCheckout() {
      return this.orders.some(item => item.selectedForCheckout);
    }
  },

  methods: {
    gotoDealPage() {
      this.$router.push('/');
    },

    async gotoProduct(pid) {
      if (!pid) return;
      // await this.done();
      this.$router.push(`/products/${pid._id}`);
    },

    async selectAllItemsForCheckout() {
      try {
        // Backup the items
        this.items_backup = _.cloneDeep(this.orders);

        // Locally select all the items
        this.orders.forEach(item => {
          // Only if item's product is available for purchase, update it selected status
          if(this.isItemAvailableForPurchase(item)) {
            item.selectedForCheckout = true;
          }
        });

        // Wait for vue recalculate
        await this.$nextTick();

        // Call to API to select all
        await this.$store.dispatch('cartStore/selectAllItemsForCheckout');
      }
      catch (error) {
        console.log(error);
        notification.error(
          this,
          this.$t('checkout.ordDet.cartUpdateErrMsg')
        );

        // Revert the items' selected status to its previous value
        for (let index = 0; index < this.orders.length; index++) {
          this.orders[index].selectedForCheckout = this.items_backup[index].selectedForCheckout;
        }
      }
    },

    async changeItemSelectedStatus(item, idx) {
      try {
        // If item's product is not available for purchase revert changes & ends function
        if(!this.isItemAvailableForPurchase(item)) {
          // Revert the item's selected status to its previous value
          this.orders[idx].selectedForCheckout = item.selectedForCheckout ? false : true;
          return;
        }

        // Wait for vue recalculate
        await this.$nextTick();

        // API call to change status
        await this.$store.dispatch(
          'cartStore/changeItemSelectedStatus',
          {
            _id: item._id,
            product: item.product._id,
            selectedForCheckout: item.selectedForCheckout
          }
        );
      }
      catch (error) {
        console.log(error);
        notification.error(
          this,
          this.$t('checkout.ordDet.cartUpdateErrMsg')
        );

        // Revert the item's selected status to its previous value
        this.orders[idx].selectedForCheckout = item.selectedForCheckout ? false : true;
      }
    },

    async updateCartItem(item) {
      this.$nextTick(async () => {
        // If item's product is not available for purchase throw error
        if(!this.isItemAvailableForPurchase(item)) {
          throw new Error('Error updating cart item: Action Disabled');
        }

        // Check if have any counts and update
        if (item.counts > 0) {
          try {
            await this.$store.dispatch('cartStore/updateOrders', [item]);
            notification.success(
              this,
              this.$t('checkout.ordDet.cartUpdateOkMsg')
            );
          } catch (err) {
            console.log('Error', err);
            notification.error(
              this,
              this.$t('checkout.ordDet.cartUpdateErrMsg')
            );
          }
        }
      });
    },

    async deleteSelected(item) {
      try {
        await this.$store.dispatch('cartStore/deleteOrders', [item]);
      } catch (err) {
        console.log(err);
      }
    },

    // Export to parents component the computed property
    getHasItemsSelectedForCheckout() {
      return this.hasItemsSelectedForCheckout;
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-bag {
  font-size: 18px;
  margin-left: 20px;
  color: #bdbdbd;
  cursor: pointer;
}

.total-line {
  padding: 10px;
}

.mask {
  opacity: 0.35;
  pointer-events: none;
  cursor: not-allowed;
}

.order-detail {
  margin-top: 30px;
  .order-empty {
    height: 500px;
    line-height: 500px;
    color: #bdbdbd;
    font-size: 1.5em;
    text-align: center;

    .content {
      display: inline-block;
      vertical-align: middle;
      line-height: normal;
    }
  }

  .orders {
    list-style-type: none;
    padding: 0px;
    width: 100%;

    .order-img {
      width: auto;
      height: 70px;
    }

    .order-desc {
      cursor: pointer;
    }

    .checkbox-item {
      display: inline !important;
    }

    .not-available {
      position: absolute;
      top: 5px;
      left: 5px;
      background-color: rgba(255, 0, 0, 0.85);
      color: white;
      padding: 2px 5px;
      font-size: 12px;
      border-radius: 3px;
      font-weight: bold;
      z-index: 1;
    }

    .item-price {
      text-align: right;

      @media (max-width: 992px) {
        text-align: left;
        margin-bottom: 0.5rem;
      }
    }

    .unit-price {
      font-size: 0.7rem;
    }

    li {
      padding: 10px;
      margin-bottom: 10px;
      width: 100%;

      &:nth-child(even) {
        background: #eeeeee;
      }
    }

    .delete {
      padding: 0.3rem;
      border-radius: 5px;
      transition: background-color 0.4s ease;
    }

    .delete:hover {
      background-color: #FFDEDD;
    }
  }
}
</style>
